import type { HTMLAttributes, ReactNode } from "react";
import { createStyles } from "./styling";

const useStyles = createStyles(({ css, token }) => ({
  actions: css`
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
    min-width: 0;
  `,
  copy: css`
    min-width: 0;
  `,
  description: css`
    align-items: center;
    color: ${token.colorTextSecondary};
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    gap: 4px 8px;
    line-height: 1.65;
    margin-top: 2px;
    overflow-wrap: anywhere;
  `,
  eyebrow: css`
    color: ${token.colorTextSecondary};
    font-size: 13px;
    line-height: 1.5;
    margin-bottom: 2px;
    overflow-wrap: anywhere;
  `,
  root: css`
    align-items: flex-end;
    display: flex;
    gap: 12px 20px;
    justify-content: space-between;
    margin-bottom: 20px;
    min-width: 0;

    @media (max-width: ${token.screenSMMax}px) {
      align-items: flex-start;
      flex-direction: column;

      .ark-section-header-actions {
        justify-content: flex-start;
        width: 100%;
      }
    }
  `,
  title: css`
    font-size: 22px;
    font-weight: 650;
    letter-spacing: 0;
    line-height: 1.35;
    margin: 0;
    overflow-wrap: anywhere;
  `,
}));

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
const headingTags = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
} as const satisfies Record<HeadingLevel, string>;

export function SectionHeader({
  actions,
  className,
  description,
  eyebrow,
  headingId,
  headingLevel = 2,
  title,
  ...props
}: Omit<HTMLAttributes<HTMLElement>, "children" | "title"> & {
  actions?: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  headingId?: string;
  headingLevel?: HeadingLevel;
  title: ReactNode;
}) {
  const { cx, styles } = useStyles();
  const Heading = headingTags[headingLevel];
  return (
    <header className={cx(styles.root, className)} {...props}>
      <div className={styles.copy}>
        {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
        <Heading className={styles.title} id={headingId}>
          {title}
        </Heading>
        {description ? (
          <div className={styles.description}>{description}</div>
        ) : null}
      </div>
      {actions ? (
        <div className={cx(styles.actions, "ark-section-header-actions")}>
          {actions}
        </div>
      ) : null}
    </header>
  );
}
